/**
 * WRAP — engine word-wrap HỢP NHẤT của /draw (đích "gộp 3 wrapper" §6 bước 3).
 *
 * Trước đây 3 vòng greedy sống song song, mỗi vòng một dị bản:
 *   · richText::layout   — token per-run, bullet/indent/tab/line-spacing (superset)
 *   · measureAndWrapText — word-greedy cho caption, track word-index cho highlight
 *   · textBox::wrap      — char-greedy cho fallback text 1-style
 * Nay CHỈ CÒN MỘT vòng break (`breakTokens`, bê nguyên từ richText::layout — gốc
 * calibrate PowerPoint); hai wrapper kia là adapter mỏng dựng token rồi gọi nó.
 * Word-index của measureText được "cắm" vào token (`wordIndex`) nên đường rich
 * cũng đánh số từ — sẵn chỗ cho caption-highlight trên rich text sau này.
 *
 * Hai renderer (drawRichText / drawCaption) vẫn tách — khác bài toán (per-run
 * style vs timing/highlight); chỉ dồn ở tầng primitive này (PLAN §4.1).
 * MỌI hằng số calibration giữ NGUYÊN GIÁ TRỊ — đo đạc so với PowerPoint PNG
 * export, đừng chỉnh nếu không có phép đo mới.
 */
import type { Ctx } from "./ctx.js";
import type { RichRun, RichParagraph } from "../schema/text.js";
import type { CaptionTextAlign } from "../schema/caption.js";
import { fallbackFamily } from "./fonts.js";

// ────────────────────────────────────────────────────────────────────────────
// Core: token + MỘT vòng greedy line-break dùng chung
// ────────────────────────────────────────────────────────────────────────────

/** Token tối thiểu mà vòng break hiểu. Adapter nào cũng quy về dạng này. */
export interface BaseTok {
  text: string;
  /** Bề rộng đã đo (px). Token tab để 0 — vòng break tự resolve theo tab stop. */
  w: number;
  /** Token toàn whitespace — không bao giờ tự kích wrap, bị trim khỏi cuối dòng. */
  space: boolean;
  /** Chứa "\t" — bề rộng resolve tại chỗ theo vị trí bút (cần opts.tabPx). */
  tab?: boolean;
  /** Ngắt dòng CỨNG (`<a:br>` → "\n"): buộc xuống dòng ngay, tạo cả dòng trống
   *  (PowerPoint vẽ `<a:br>` đuôi đoạn thành một dòng trống). Không phải nội dung. */
  br?: boolean;
  /** Số thứ tự từ (token không-space) trong nguồn — cho caption highlight. */
  wordIndex?: number;
}

/** Một dòng thô sau break: token CHƯA trim đuôi (giữ nguyên để bên rich còn đo
 *  line-height trên cả space-run cuối dòng như bản gốc) — gọi `trimLineEnd`. */
export interface BrokenLine<T extends BaseTok> { toks: T[]; width: number }

/**
 * Vòng greedy break DUY NHẤT (nguồn: richText::layout). Ngữ nghĩa giữ nguyên:
 * token không-space vượt bề rộng (hoặc chạm trần số từ) ⇒ xuống dòng TRƯỚC nó;
 * token space không bao giờ tự kích wrap (nó tràn tạm rồi bị trim ở đuôi dòng);
 * tab resolve bề rộng tại chỗ = khoảng tới tab-stop kế (mutate `tk.w` — vị trí
 * bút tính từ indent thật của dòng: dòng đầu đoạn dùng `firstIndent`, dòng wrap
 * dùng `hangIndent`). Dòng cuối luôn được đẩy ra, kể cả rỗng (đoạn rỗng = một
 * dòng trống — caller nào không muốn thì tự lọc).
 */
export function breakTokens<T extends BaseTok>(
  toks: T[],
  maxW: number,
  opts: { maxWordsPerLine?: number; tabPx?: number; firstIndent?: number; hangIndent?: number } = {},
): BrokenLine<T>[] {
  const lines: BrokenLine<T>[] = [];
  const maxWords = opts.maxWordsPerLine ?? Infinity;
  let cur: T[] = [], curW = 0, words = 0;
  // Các ký tự của MỘT từ bị ngắt giữa mang cùng `wordIndex`; đếm chúng là một
  // từ, nếu không `maxWordsPerLine` băm một URL dài thành mỗi dòng vài ký tự.
  let lastWordIndex: number | undefined;
  for (const tk of toks) {
    // Hard break: end this line now (even if empty → a blank line) and drop the
    // break token itself. Must precede the width/tab logic.
    if (tk.br) { lines.push({ toks: cur, width: curW }); cur = []; curW = 0; words = 0; lastWordIndex = undefined; continue; }
    if (tk.tab && opts.tabPx) {
      const pos = (lines.length === 0 ? (opts.firstIndent ?? 0) : (opts.hangIndent ?? 0)) + curW;
      tk.w = Math.max(0, Math.ceil((pos + 1) / opts.tabPx) * opts.tabPx - pos);
    }
    if ((curW + tk.w > maxW || words >= maxWords) && cur.length && !tk.space) {
      lines.push({ toks: cur, width: curW });
      cur = []; curW = 0; words = 0; lastWordIndex = undefined;
    }
    cur.push(tk); curW += tk.w;
    if (!tk.space) {
      if (tk.wordIndex === undefined || tk.wordIndex !== lastWordIndex) words++;
      lastWordIndex = tk.wordIndex;
    }
  }
  lines.push({ toks: cur, width: curW });
  return lines;
}

/** Trim token space ở đuôi dòng, trừ lại bề rộng (= trimTrailingSpace gốc). */
export const trimLineEnd = (ln: { toks: BaseTok[]; width: number }): void => {
  while (ln.toks.length && ln.toks[ln.toks.length - 1].space) { ln.width -= ln.toks[ln.toks.length - 1].w; ln.toks.pop(); }
};

// ────────────────────────────────────────────────────────────────────────────
// Rich half — layout token per-run (nguồn richText.ts, dời nguyên khối)
// ────────────────────────────────────────────────────────────────────────────

export interface Tok extends BaseTok { run: RichRun }
export interface Line { toks: Tok[]; width: number; height: number; para: RichParagraph; paraIndex: number; indent: number; firstOfPara: boolean; lastOfPara: boolean; spaceBefore: number;
  /** Line-spacing leading reserved ABOVE the glyphs (px) — the gap between the
   *  line's box height and the font's own ascent+descent box. PowerPoint puts this
   *  leading above each line, so the glyphs are drawn at `y + spaceBefore + lead`.
   *  0 when the box height does not exceed the glyph box (tight/large fonts). */
  lead: number; }

// Default tab stop: OOXML defTabSz defaults to 914400 EMU (1 inch) = 96px at our
// 96-dpi native px. Tab positions are absolute (not font-scaled), so a tab
// advances the pen to the next multiple of this from the text area's left edge.
const TAB_PX = 96;
// PowerPoint renders a baseline-shifted run (super/subscript) at a reduced size.
// (Dùng chung với richText.ts: đo ở đây, vẽ ở đó — lệch là layout ≠ draw.)
export const SUBSUP_SCALE = 0.65;
// PowerPoint's percentage line spacing (<a:lnSpc><a:spcPct>) is relative to a
// "single line" of 1.2 × font size — NOT the font's own metric line height — so a
// line's pitch = spcPct × 1.2 × fontPx. Verified against the T1.2 PowerPoint PNG
// export: the 44pt question on slide 20 inherits spcPct 130% and its measured
// line pitch is 1.30 × 1.20 × em (the earlier 1.25 base rendered ~4% too loose).
const PPT_SINGLE_LINE_FACTOR = 1.2;
// PowerPoint reserves a line's leading (the gap between its box height and the
// glyph's own ascent+descent box) ABOVE the glyphs, so the first line of a
// paragraph sits one leading below the box top (measured ~13px on slide 20's
// 130%-spaced 44pt text). We approximate the glyph box height as this fraction of
// the em when real font metrics are unavailable.
const GLYPH_BOX_EM = 1.2;
// Sanity floor for a font's glyph box (ascent+descent) as a fraction of the em: no
// real font's line box is below ~1.0em, so a smaller measured value means a broken
// fontBoundingBox (e.g. Coiny's negative descent) — clamp it so `lead` isn't inflated.
const GLYPH_BOX_MIN = 1.0;
// Heuristic hanging-bullet indent for a bulleted paragraph with NO explicit
// <a:pPr marL> to honour: reserve a gap of (box width × this fraction) + a fixed
// pad px, so the bullet hangs to the left of the wrapped text margin. Tuned to
// approximate PowerPoint's default bullet indent across sizes.
const BULLET_INDENT_W_FRAC = 0.045;
const BULLET_INDENT_PAD_PX = 8;

/** Effective drawn size factor for a run (shrunk when super/subscripted). */
export const runScale = (run: RichRun): number => (run.baseline ? SUBSUP_SCALE : 1);

/** Font string cho một run — đo (layout) và vẽ (drawRichText) PHẢI dùng chung. */
export function fontStr(run: RichRun, scale: number): string {
  return `${run.italic ? "italic " : ""}${run.bold ? "bold " : ""}${run.sizePt * scale * runScale(run)}px "${run.family}", "${fallbackFamily()}", sans-serif`;
}

export function layout(ctx: Ctx, paras: RichParagraph[], maxW: number, scale: number, opts?: { tabPx?: number; noWrap?: boolean; wrapTolW?: number; charBreak?: boolean }): Line[] {
  const lines: Line[] = [];
  const tabSize = opts?.tabPx ?? TAB_PX; // <a:bodyPr defTabSz> override (px), else the OOXML 1-inch default
  // paraIndex tracks the paragraph's position in <a:p> document order (including
  // empty paragraphs) so a build's pRg index can gate the right lines (P2.1).
  for (let paraIndex = 0; paraIndex < paras.length; paraIndex++) {
    const para = paras[paraIndex];
    // Text indent. When the paragraph declares its own <a:pPr marL>, honour it
    // (px) so a numbered/bulleted item's text aligns to the real margin; else fall
    // back to the heuristic hanging indent for bulleted paragraphs.
    const hasMarL = para.marLPx != null;
    const bulletW = hasMarL ? para.marLPx! : para.bullet ? maxW * BULLET_INDENT_W_FRAC + BULLET_INDENT_PAD_PX : 0;
    // First line's indent: a bulleted para keeps text at marL (the bullet hangs to
    // its left); a bulletless hanging para (marL+negative indent) starts its first
    // line at marL+indent (e.g. "1.1." sits left of the wrapped text margin).
    const firstIndent = para.bullet || !hasMarL ? bulletW : Math.max(0, para.marLPx! + (para.indentPx ?? 0));
    // <a:bodyPr wrap="none"> → text doesn't wrap, it overflows. Else wrap at the
    // box width (plus a small spAutoFit tolerance for our slightly-wider metrics).
    const lineMaxW = opts?.noWrap ? Number.POSITIVE_INFINITY : maxW * (1 + (opts?.wrapTolW ?? 0)) - bulletW;
    const maxSize = Math.max(8, ...para.runs.map((r) => r.sizePt * scale));
    // per-paragraph line spacing: <a:lnSpc> spcPct is relative to a single line of
    // 1.2× font size (PPT_SINGLE_LINE_FACTOR), so no-lnSpc ⇒ 1.2, 150% ⇒ 1.8; or
    // spcPts (absolute pt → px, autofit-aware via `scale`); plus <a:spcBef>/
    // <a:spcAft> inter-paragraph spacing.
    const lhFactor = (para.lineSpacePct ?? 1) * PPT_SINGLE_LINE_FACTOR;
    const lineHpx = (px: number) => (para.lineSpacePts != null ? para.lineSpacePts * scale : px * lhFactor);
    // The font's own ascent+descent box (px) for a run, for the leading-above split.
    const maxRunFor = (run: RichRun | undefined) => run ?? para.runs.reduce((a, b) => (b.sizePt > (a?.sizePt ?? 0) ? b : a), para.runs[0]);
    const glyphBox = (run: RichRun | undefined): number => {
      const r = maxRunFor(run); if (!r) return maxSize * GLYPH_BOX_EM;
      const fontPx = r.sizePt * scale;
      ctx.font = fontStr(r, scale);
      const m = ctx.measureText("Mg");
      const h = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
      // Some fonts ship a BROKEN fontBoundingBox (e.g. Coiny reports a NEGATIVE
      // fontBoundingBoxDescent → h ≈ 0.5em). Trusting it inflates `lead` (lineH −
      // glyphBox) and shoves the line far down — slide11's Coiny "Giọng đọc" title
      // dropped ~16px onto the body below. Floor the box at a sane GLYPH_BOX_MIN×em
      // so a bad metric can't; real fonts (≥1.0em) are unaffected.
      return Math.max(h > 0 ? h : fontPx * GLYPH_BOX_EM, fontPx * GLYPH_BOX_MIN);
    };
    const leadOf = (lineH: number, run: RichRun | undefined) => Math.max(0, lineH - glyphBox(run));
    // spcBef/spcAft: absolute pts (×scale) + a fraction of the single line height.
    const lhBase = lineHpx(maxSize);
    const spcBefPx = (para.spaceBeforePts ?? 0) * scale + (para.spaceBeforePct ?? 0) * lhBase;
    const spcAftPx = (para.spaceAfterPts ?? 0) * scale + (para.spaceAfterPct ?? 0) * lhBase;
    if (para.runs.length === 0) {
      lines.push({ toks: [], width: 0, height: lineHpx(maxSize) + spcBefPx + spcAftPx, para, paraIndex, indent: bulletW, firstOfPara: true, lastOfPara: true, spaceBefore: spcBefPx, lead: leadOf(lineHpx(maxSize), undefined) });
      continue;
    }

    const toks: Tok[] = [];
    let wordIndex = 0; // đánh số từ trong đoạn — token engine mang word-index (§6 bước 3)
    for (const run of para.runs) {
      ctx.font = fontStr(run, scale);
      // Hard line breaks (<a:br> → "\n"): split the run on newlines and emit a
      // `br` token between segments so breakTokens starts a new (possibly empty)
      // line. A trailing "\n" therefore yields a blank line, like PowerPoint.
      const segments = run.text.split("\n");
      for (let si = 0; si < segments.length; si++) {
        if (si > 0) toks.push({ text: "", run, w: 0, space: false, br: true });
        for (const piece of segments[si].split(/(\s+)/)) {
          if (piece === "") continue;
          // A tab advances to the next tab stop; its width is resolved at placement
          // time (it depends on the pen's position within the line), so 0 for now.
          const tab = piece.includes("\t");
          const space = /^\s+$/.test(piece);
          const w = tab ? 0 : ctx.measureText(piece).width;
          // A single non-space word wider than the whole line (e.g. a long URL with
          // no spaces) can never fit and would spill out of the box — PowerPoint
          // breaks such a word across lines, so split it into per-character tokens
          // the greedy loop can wrap. Guarded on w > lineMaxW so ordinary words stay
          // whole; skipped under wrap="none" (lineMaxW = ∞, so nothing overflows it).
          // Gated on charBreak (spAutoFit boxes, which PowerPoint grew to hug wrapped
          // text): other boxes keep the width-shrink fallback for over-long tokens.
          if (opts?.charBreak && !space && !tab && piece.length > 1 && w > lineMaxW) {
            const wi = wordIndex++;
            for (const ch of piece) toks.push({ text: ch, run, w: ctx.measureText(ch).width, space: false, wordIndex: wi });
          } else {
            toks.push({ text: piece, run, w, space, tab, ...(space ? {} : { wordIndex: wordIndex++ }) });
          }
        }
      }
    }
    const start = lines.length;
    const broken = breakTokens(toks, lineMaxW, { tabPx: tabSize, firstIndent, hangIndent: bulletW });
    for (let i = 0; i < broken.length; i++) {
      const bl = broken[i];
      // line-height đo trên token CHƯA trim (kể cả space-run đuôi dòng) — đúng
      // thứ tự bản gốc: track lúc gom, trim sau.
      let lineHt = 0; let maxRun: RichRun | undefined;
      for (const tk of bl.toks) if (tk.run.sizePt * scale >= lineHt) { lineHt = tk.run.sizePt * scale; maxRun = tk.run; }
      const h = lineHpx(lineHt || maxSize);
      const ln: Line = { toks: bl.toks, width: bl.width, height: h, para, paraIndex, indent: bulletW, firstOfPara: false, lastOfPara: i === broken.length - 1, spaceBefore: 0, lead: leadOf(h, maxRun) };
      trimLineEnd(ln); lines.push(ln);
    }
    // spcBef as leading on the first line, spcAft as trailing on the last
    if (lines[start]) { lines[start].firstOfPara = true; lines[start].indent = firstIndent; lines[start].height += spcBefPx; lines[start].spaceBefore = spcBefPx; }
    lines[lines.length - 1].height += spcAftPx;
  }
  return lines;
}

// ────────────────────────────────────────────────────────────────────────────
// Caption half — adapter word-greedy giữ nguyên hợp đồng measureAndWrapText
// ────────────────────────────────────────────────────────────────────────────

export interface MeasuredWord {
  text: string;
  x: number;
  y: number;
  width: number;
  wordIndex: number;
}

export interface MeasuredLine {
  text: string;
  words: MeasuredWord[];
  width: number;
  /** Lề trái của dòng trong khối, do `align` quyết định. */
  x: number;
  y: number;
}

export interface WrapCaptionOptions {
  /** Bề rộng KHỐI (đã qua sàn động của captionLayout), px trên khung. */
  maxWidth: number;
  /** Cỡ chữ đã nhân scale — chỉ dùng cho bề rộng space dự phòng. */
  fontSize: number;
  lineHeight: number;
  align: CaptionTextAlign;
  /** Trần số từ mỗi dòng. Vắng / `0` / âm ⇒ không giới hạn. */
  maxWordsPerLine?: number;
  /** Ngắt GIỮA KÝ TỰ từ nào rộng hơn `maxWidth` (URL, chuỗi không dấu cách,
   *  hoặc cỡ chữ rất lớn). Không bật thì từ đó tràn khỏi khối. */
  charBreak?: boolean;
  /** Bề rộng đã đo của từng từ, cùng thứ tự `words`. Caller nào đã đo để tính
   *  sàn động thì truyền lại, khỏi đo lượt hai. */
  widths?: number[];
  /** Chỉ số từ mà ngay sau nó nguồn đã xuống dòng — ngắt CỨNG ở đó. */
  breakAfter?: ReadonlySet<number>;
}

/**
 * Đo và ngắt dòng cho caption. Caller PHẢI set `ctx.font` trước — nó cũng cần
 * font đó để đo từ dài nhất cho sàn động, nên đặt hai lần là mời lệch nhau.
 *
 * Adapter trên `breakTokens`: từ = token thường, khoảng cách = space-token bề
 * rộng cố định (đo từ font, fallback 0.25em) — điều kiện wrap ra đúng
 * `lineWidth + spaceWidth + w > maxWidth` của bản word-greedy cũ.
 */
export function measureAndWrapText(
  ctx: CanvasRenderingContext2D,
  words: string[],
  opts: WrapCaptionOptions,
): MeasuredLine[] {
  const { maxWidth, fontSize, lineHeight, align } = opts;
  // Dự phòng 0.25em khi font khai space rộng 0 — giữ nguyên giá trị bản gốc.
  const spaceWidth = ctx.measureText(' ').width || fontSize * 0.25;

  const toks: BaseTok[] = [];
  words.forEach((text, idx) => {
    if (idx > 0) {
      if (opts.breakAfter?.has(idx - 1))
        toks.push({ text: '', w: 0, space: false, br: true });
      else toks.push({ text: ' ', w: spaceWidth, space: true });
    }
    const w = opts.widths?.[idx] ?? ctx.measureText(text).width;
    // Từ rộng hơn cả khối không bao giờ vừa, nên tách thành token từng ký tự cho
    // vòng greedy ngắt được. Giữ nguyên wordIndex để highlight vẫn trỏ đúng từ.
    if (opts.charBreak && text.length > 1 && w > maxWidth) {
      for (const ch of text) {
        toks.push({ text: ch, w: ctx.measureText(ch).width, space: false, wordIndex: idx });
      }
    } else {
      toks.push({ text, w, space: false, wordIndex: idx });
    }
  });
  if (toks.length === 0) return [];

  // `?? Infinity` để 0 lọt qua thành trần 0 và chốt mỗi dòng một từ; chuẩn hoá ở
  // đây vì breakTokens là primitive dùng chung với đường rich-text.
  const maxWordsPerLine =
    opts.maxWordsPerLine && opts.maxWordsPerLine > 0 ? opts.maxWordsPerLine : Infinity;
  const broken = breakTokens(toks, maxWidth, { maxWordsPerLine });

  return broken.map((bl, i) => {
    trimLineEnd(bl);
    const wordToks = bl.toks.filter((t) => !t.space);

    let lineX = 0;
    if (align === 'CENTER') lineX = (maxWidth - bl.width) / 2;
    else if (align === 'RIGHT') lineX = maxWidth - bl.width;

    let x = lineX;
    let prevWordIndex: number | undefined;
    const measured: MeasuredWord[] = wordToks.map((t) => {
      // Chỉ chèn space GIỮA hai từ khác nhau. Các ký tự của một từ bị ngắt giữa
      // đứng liền nhau, cộng space vào đây thì hộp highlight rộng ra theo số ký
      // tự và mọi từ sau nó trên cùng dòng cũng lệch.
      if (prevWordIndex !== undefined && t.wordIndex !== prevWordIndex) x += spaceWidth;
      const mw: MeasuredWord = { text: t.text, x, y: i * lineHeight, width: t.w, wordIndex: t.wordIndex! };
      x += t.w;
      prevWordIndex = t.wordIndex;
      return mw;
    });

    // Ký tự của một từ bị ngắt giữa không được nối lại bằng space.
    const text = wordToks.reduce(
      (acc, t, ti) =>
        ti > 0 && t.wordIndex === wordToks[ti - 1].wordIndex ? acc + t.text : acc ? `${acc} ${t.text}` : t.text,
      "",
    );
    return { text, words: measured, width: bl.width, x: lineX, y: i * lineHeight };
  });
}
